<script setup lang="ts">
  // Ported from cartadigital-pos app/components/pos/order/PosOrderItemModal.vue.
  // Opened via showDynamicDialog<IPosV2ItemModalResult | null>(..., 'pos-item-options',
  // { width: 900 }). All selection writes are immutable (Vue 2 reactivity).
  import { computed, getCurrentInstance, ref } from 'vue';
  import { useMediaQuery } from '@vueuse/core';
  import { TaxDisplayMode } from '@/enums/TaxDisplayMode';
  import { IItem } from '@/interfaces/IItem';
  import { adaptItemOptions } from '@/domain/pos/option-adapter';
  import {
    hydrateStoredSelectedOptions,
    ISelectedOptionsMap,
    validateSelectedOptions,
  } from '@/domain/pos/item-options-validation';
  import { computeItemUnitPrice } from '@/domain/pos/pricing/item-price';
  import { POS_MAX_LINE_QUANTITY } from '@/domain/pos/order-lines';
  import type {
    IMenuOption,
    IMenuOptionItem,
    IPosCartLine,
  } from '@/domain/pos/pos-types';
  import { roundPrice } from '@/utils';
  import { useModal } from '@/composition/use-modal';
  import { usePosMoney } from '@/composition/use-pos-money';
  import { useUi } from '@/composition/use-ui';
  import { AlertType } from '@/interfaces/IAlert';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiStepper from '@/components/pos-v2/ui/PosUiStepper.vue';
  import PosUiTextarea from '@/components/pos-v2/ui/PosUiTextarea.vue';
  import PosV2ItemImage from '@/components/pos-v2/order/PosV2ItemImage.vue';

  export interface IPosV2ItemModalResult {
    optionValues: ISelectedOptionsMap;
    quantity: number;
    userComment?: string;
  }

  const props = defineProps<{
    item: IItem;
    editingLine?: IPosCartLine;
  }>();

  const modals = useModal();
  const money = usePosMoney();
  const ui = useUi();
  const restaurantStore = useRestaurantStore();

  // Tailwind `sm`: 44px option steppers from tablets up; phones keep the
  // compact size so name, price and stepper still share the row. Width-based,
  // so the on-screen keyboard never flips it.
  const isSmUp = useMediaQuery('(min-width: 640px)');

  // vue-i18n in script (Vue 2.7): translate through the component proxy.
  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const itemOptions = computed<IMenuOption[]>(() =>
    adaptItemOptions(props.item)
  );

  const quantity = ref(props.editingLine?.quantity ?? 1);
  const userComment = ref(props.editingLine?.userComment ?? '');

  // Hydrate stored selections against the live catalog: ghost ids (menu
  // edited after the line was added) would otherwise inflate the selected
  // count and freeze maxed `multiple` groups.
  const hydration = props.editingLine
    ? hydrateStoredSelectedOptions(
        itemOptions.value,
        props.editingLine.optionValues
      )
    : null;
  const selectedOptions = ref<ISelectedOptionsMap>(hydration?.hydrated ?? {});

  if (hydration?.droppedAny) {
    ui.showToast(AlertType.Info, i18nT('posV2.optionsChangedToast'));
  }

  // Validation (single source of truth)
  const validation = computed(() =>
    validateSelectedOptions(itemOptions.value, selectedOptions.value)
  );
  const isValid = computed(() => validation.value.ok);
  const validationErrors = computed(() => validation.value.errorsByOptionId);

  const unitPrice = computed(() =>
    computeItemUnitPrice({
      item: props.item,
      itemOptions: itemOptions.value,
      selectedOptions: validation.value.sanitized,
      taxPercent: restaurantStore.restaurant?.tax ?? null,
      taxMode:
        restaurantStore.restaurantSettings?.taxMode ?? TaxDisplayMode.NoTax,
    })
  );

  const totalPrice = computed(() =>
    roundPrice(unitPrice.value * quantity.value)
  );

  function getSelectedValue(optionId: number, optionItemId: number): number {
    return selectedOptions.value[String(optionId)]?.[String(optionItemId)] ?? 0;
  }

  function getSelectedCount(optionId: number): number {
    const selectedItems = selectedOptions.value[String(optionId)] || {};
    return Object.values(selectedItems).reduce((acc, val) => acc + val, 0);
  }

  /** Immutable write (Vue 2 cannot track new nested keys). */
  function setSelectedValue(
    optionId: number,
    optionItemId: number,
    val: number
  ) {
    const optionKey = String(optionId);
    selectedOptions.value = {
      ...selectedOptions.value,
      [optionKey]: {
        ...(selectedOptions.value[optionKey] ?? {}),
        [String(optionItemId)]: val,
      },
    };
  }

  function getOptionItemPriceLabel(
    option: IMenuOption,
    optionItem: IMenuOption['items'][number]
  ): string | null {
    return money.formatVariantLabel({
      optionMain: option.main,
      optionPrice: optionItem.discountedCost ?? optionItem.cost,
      baseItemPrice: props.item.discountedPrice ?? props.item.price,
    });
  }

  /** Radio glyph for single-choice groups, checkbox glyph for multi-select. */
  function optionIndicatorIcon(
    option: IMenuOption,
    optionItemId: number
  ): string {
    const selected = getSelectedValue(option.id, optionItemId) > 0;
    if (option.multiple) {
      return selected ? 'checkbox-marked' : 'checkbox-blank-outline';
    }
    return selected ? 'radiobox-marked' : 'radiobox-blank';
  }

  function handleOptionToggle(option: IMenuOption, optionItemId: number) {
    if (option.multiple) {
      const current = getSelectedValue(option.id, optionItemId);
      if (current > 0) {
        setSelectedValue(option.id, optionItemId, 0);
      } else {
        const currentSum = getSelectedCount(option.id);
        if (option.max > 0 && currentSum + 1 > option.max) {
          ui.showToast(
            AlertType.Info,
            i18nT('posV2.maxOptionsToast', { max: option.max })
          );
          return;
        }
        setSelectedValue(option.id, optionItemId, 1);
      }
    } else {
      // Radio-like: clear others in the group and set this one.
      selectedOptions.value = {
        ...selectedOptions.value,
        [String(option.id)]: { [String(optionItemId)]: 1 },
      };
    }
  }

  /**
   * Most an option item's quantity can be: the group's remaining room (its max
   * minus what the other items already take), the item's own max and the line
   * cap, matching the validator. Never below the current value, so a stored
   * quantity above a since-lowered max can still be stepped down, not wiped.
   */
  function getOptionItemMax(option: IMenuOption, optionItemId: number): number {
    const current = getSelectedValue(option.id, optionItemId);
    const groupRoom =
      option.multiple && option.max > 0
        ? option.max - (getSelectedCount(option.id) - current)
        : Infinity;
    const optionItem = option.items.find((i) => i.id === optionItemId);
    const itemMax =
      optionItem && optionItem.max > 0 ? optionItem.max : Infinity;
    return Math.max(
      current,
      Math.min(groupRoom, itemMax, POS_MAX_LINE_QUANTITY)
    );
  }

  /** Unselected rows the group's max leaves no room for. */
  function isOptionItemLocked(
    option: IMenuOption,
    optionItemId: number
  ): boolean {
    return (
      option.multiple &&
      option.max > 0 &&
      getSelectedValue(option.id, optionItemId) === 0 &&
      getSelectedCount(option.id) >= option.max
    );
  }

  function getOptionQuantityLabel(optionItem: IMenuOptionItem): string {
    return `${i18nT('posV2.quantity')}: ${optionItem.title}`;
  }

  /** Typed more than the row allows: name the limit that won. */
  function handleOptionClamped(
    option: IMenuOption,
    optionItem: IMenuOptionItem
  ) {
    const groupFull =
      option.multiple &&
      option.max > 0 &&
      getSelectedCount(option.id) >= option.max;
    if (groupFull) {
      ui.showToast(
        AlertType.Info,
        i18nT('posV2.maxOptionsToast', { max: option.max })
      );
    } else if (optionItem.max > 0) {
      ui.showToast(
        AlertType.Info,
        i18nT('posV2.errOptItemMax', {
          max: optionItem.max,
          title: optionItem.title,
        })
      );
    }
  }

  /** Stepper-driven quantity; 0 deselects the item. Bounds restated as defense-in-depth. */
  function handleOptionQuantity(
    option: IMenuOption,
    optionItemId: number,
    value: number
  ) {
    const bounded = Math.max(
      0,
      Math.min(getOptionItemMax(option, optionItemId), value)
    );
    setSelectedValue(option.id, optionItemId, bounded);
  }

  function handleSave() {
    if (!isValid.value) {
      const firstError = validation.value.firstError;
      ui.showToast(
        AlertType.Error,
        firstError
          ? i18nT(firstError.key, firstError.params)
          : i18nT('posV2.reviewOptions')
      );
      return;
    }

    modals.dialogSubmit('pos-item-options', <IPosV2ItemModalResult>{
      optionValues: validation.value.sanitized,
      quantity: quantity.value,
      userComment: userComment.value || undefined,
    });
  }

  function handleClose() {
    modals.hide('pos-item-options');
  }
</script>

<template>
  <div class="pos-v2-item-modal">
    <!-- Header -->
    <div class="modal-header">
      <div class="header-content">
        <div v-if="item.picture" class="header-image">
          <PosV2ItemImage :item="item" loading="eager" />
        </div>
        <h2 class="header-title">{{ item.name }}</h2>
      </div>
      <PosUiButton
        icon="close"
        color="neutral"
        variant="ghost"
        size="md"
        @click="handleClose"
      />
    </div>

    <!-- Body -->
    <div class="modal-body">
      <div class="body-columns">
        <!-- Left: details -->
        <div class="details-column">
          <div class="item-image-large">
            <PosV2ItemImage :item="item" loading="eager" class="large-image" />
          </div>

          <div v-if="item.price > 0" class="price-block">
            <div v-if="item.discountedPrice" class="price-original">
              {{ money.formatCatalogMoney(item.price) }}
            </div>
            <div class="price-current">
              {{ money.formatCatalogMoney(item.discountedPrice || item.price) }}
            </div>
          </div>

          <p v-if="item.description" class="item-description">
            {{ item.description }}
          </p>
        </div>

        <!-- Right: options + comment -->
        <div class="options-column">
          <div v-if="itemOptions.length > 0" class="options-list">
            <div
              v-for="option in itemOptions"
              :key="option.id"
              class="option-group"
              :class="{ invalid: validationErrors[option.id] }"
            >
              <div class="option-header">
                <span class="option-title">{{ option.title }}</span>
                <div class="option-badges">
                  <PosUiBadge
                    v-if="option.mandatory"
                    :label="$t('posV2.mandatory')"
                    color="error"
                    variant="soft"
                    size="xs"
                  />
                  <PosUiBadge
                    v-else-if="option.min > 0"
                    :label="$t('posV2.minBadge', { min: option.min })"
                    color="warning"
                    variant="soft"
                    size="xs"
                  />
                  <PosUiBadge
                    v-if="option.multiple && option.max > 0"
                    :label="$t('posV2.maxBadge', { max: option.max })"
                    color="info"
                    variant="soft"
                    size="xs"
                  />
                  <PosUiBadge
                    v-if="getSelectedCount(option.id) > 0"
                    :label="`${getSelectedCount(option.id)}/${
                      option.max || '∞'
                    }`"
                    color="primary"
                    variant="solid"
                    size="xs"
                  />
                </div>
              </div>

              <div v-if="validationErrors[option.id]" class="option-error">
                {{
                  $t(
                    validationErrors[option.id].key,
                    validationErrors[option.id].params
                  )
                }}
              </div>

              <div class="option-items">
                <div
                  v-for="optionItem in option.items"
                  :key="optionItem.id"
                  class="option-item"
                  :class="{
                    selected: getSelectedValue(option.id, optionItem.id) > 0,
                    locked: isOptionItemLocked(option, optionItem.id),
                  }"
                >
                  <button
                    class="option-item-button"
                    type="button"
                    @click="handleOptionToggle(option, optionItem.id)"
                  >
                    <span class="option-item-main">
                      <PosUiIcon
                        class="option-item-indicator"
                        :name="optionIndicatorIcon(option, optionItem.id)"
                        :size="20"
                      />
                      <span class="option-item-name">
                        {{ optionItem.title }}
                      </span>
                    </span>
                    <span
                      v-if="getOptionItemPriceLabel(option, optionItem)"
                      class="option-item-price"
                    >
                      {{ getOptionItemPriceLabel(option, optionItem) }}
                    </span>
                  </button>

                  <div
                    v-if="
                      option.multiple &&
                      getSelectedValue(option.id, optionItem.id) > 0
                    "
                    class="option-item-stepper"
                  >
                    <PosUiStepper
                      :value="getSelectedValue(option.id, optionItem.id)"
                      :min="0"
                      :max="getOptionItemMax(option, optionItem.id)"
                      :size="isSmUp ? 'md' : 'sm'"
                      editable
                      :aria-label="getOptionQuantityLabel(optionItem)"
                      @input="
                        (v) => handleOptionQuantity(option, optionItem.id, v)
                      "
                      @clamped="handleOptionClamped(option, optionItem)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Per-line comment -->
          <div class="item-comment">
            <label class="comment-label">
              <PosUiIcon name="message-outline" />
              <span>{{ $t('posV2.itemComment') }}</span>
              <span class="optional">{{ $t('posV2.optional') }}</span>
            </label>
            <PosUiTextarea
              :value="userComment"
              :placeholder="$t('posV2.itemCommentPlaceholder')"
              :rows="2"
              :maxlength="140"
              @input="(v) => (userComment = v)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="modal-footer">
      <PosUiStepper
        :value="quantity"
        :min="1"
        :max="POS_MAX_LINE_QUANTITY"
        size="lg"
        editable
        :aria-label="$t('posV2.quantity')"
        @input="(v) => (quantity = v)"
      />

      <PosUiButton
        color="primary"
        variant="solid"
        size="xl"
        :disabled="!isValid"
        icon="check"
        class="save-button"
        @click="handleSave"
      >
        {{ editingLine ? $t('posV2.saveChanges') : $t('posV2.add') }} —
        {{ money.formatComputedMoney(totalPrice) }}
      </PosUiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
  // Root lives inside .vm--modal.dialog--height-limit (a max-height flex
  // parent): it stretches to the limit and the body scrolls internally.
  .pos-v2-item-modal {
    @apply tw-flex tw-min-h-0 tw-w-full tw-flex-col;

    .modal-header {
      @apply tw-flex tw-flex-shrink-0 tw-items-start tw-justify-between tw-gap-3 tw-border-b tw-border-neutral-200 tw-px-4 tw-py-3;

      .header-content {
        @apply tw-flex tw-flex-1 tw-items-center tw-gap-3;

        .header-image {
          @apply tw-h-12 tw-w-12 tw-flex-shrink-0 tw-overflow-hidden tw-rounded-lg;
        }

        .header-title {
          @apply tw-text-lg tw-font-bold tw-text-neutral-900 md:tw-text-xl;
        }
      }
    }

    .modal-body {
      @apply tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-px-4 tw-py-3;
    }

    .body-columns {
      @apply tw-flex tw-flex-col tw-gap-4 lg:tw-flex-row lg:tw-gap-6;
    }

    .details-column {
      @apply tw-flex tw-flex-row tw-items-start tw-gap-3 lg:tw-w-64 lg:tw-flex-shrink-0 lg:tw-flex-col;

      .item-image-large {
        @apply tw-h-16 tw-w-16 tw-flex-shrink-0 tw-overflow-hidden tw-rounded-lg lg:tw-aspect-square lg:tw-h-auto lg:tw-w-full;

        .large-image {
          @apply tw-h-full tw-w-full;
        }
      }

      .price-block {
        @apply tw-flex tw-flex-1 tw-items-center tw-gap-2 lg:tw-flex-none;

        .price-original {
          @apply tw-text-base tw-text-neutral-500 tw-line-through;
        }

        .price-current {
          @apply tw-text-2xl tw-font-bold tw-text-orange-600;
        }
      }

      .item-description {
        @apply tw-hidden lg:tw-block lg:tw-text-sm lg:tw-leading-relaxed lg:tw-text-neutral-700;
      }
    }

    .options-column {
      @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-3;
    }

    .options-list {
      @apply tw-flex tw-flex-col tw-gap-4;
    }

    .option-group {
      @apply tw-flex tw-flex-col tw-gap-2 tw-rounded-lg tw-border tw-border-neutral-200 tw-p-3 tw-transition-colors;

      &.invalid {
        @apply tw-border-red-600;
      }
    }

    .option-header {
      @apply tw-flex tw-items-center tw-justify-between tw-gap-2;

      .option-title {
        @apply tw-text-sm tw-font-semibold tw-text-neutral-900;
      }

      .option-badges {
        @apply tw-flex tw-flex-wrap tw-items-center tw-gap-1;
      }
    }

    .option-error {
      @apply tw-text-xs tw-text-red-600;
    }

    .option-items {
      @apply tw-flex tw-flex-col tw-gap-1.5;
    }

    /*
     * The row container owns the outline + selected skin so the qty stepper
     * renders inside it; the inner <button> stays a transparent hit area
     * (interactive controls can't nest inside a native button). The .selected
     * block must stay last: its indicator/name overrides tie on specificity
     * with the base rules and win by source order.
     */
    .option-item {
      @apply tw-flex tw-items-center tw-gap-2;
      @apply tw-rounded-lg tw-border-2 tw-border-neutral-200 tw-bg-white;
      @apply tw-transition-all hover:tw-bg-neutral-100 active:tw-bg-neutral-200;

      .option-item-button {
        // sm+: matches the 46px `md` stepper pill so selected rows do not grow.
        @apply tw-flex tw-min-h-[44px] tw-flex-1 tw-items-center tw-justify-between tw-gap-2 tw-px-3 tw-py-2.5 tw-text-left sm:tw-min-h-[46px];

        .option-item-main {
          @apply tw-flex tw-min-w-0 tw-items-center tw-gap-2;
        }

        .option-item-indicator {
          @apply tw-flex-shrink-0 tw-text-neutral-400;
        }

        .option-item-name {
          @apply tw-text-sm tw-font-medium tw-text-neutral-700;
        }

        .option-item-price {
          @apply tw-flex-shrink-0 tw-text-xs tw-font-semibold tw-text-orange-600;
        }
      }

      // Side padding keeps a dead zone between "−" and the toggle button so a
      // near miss never deselects the row.
      .option-item-stepper {
        @apply tw-flex tw-flex-shrink-0 tw-items-center tw-px-2;
      }

      &.locked {
        @apply tw-opacity-60;
      }

      &.selected {
        @apply tw-border-orange-600/40 tw-bg-orange-50;

        .option-item-indicator {
          @apply tw-text-orange-600;
        }

        .option-item-name {
          @apply tw-font-semibold tw-text-neutral-900;
        }
      }
    }

    .item-comment {
      @apply tw-flex tw-flex-col tw-gap-1.5 tw-border-t tw-border-neutral-200 tw-pt-3;

      .comment-label {
        @apply tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-medium tw-text-neutral-700;

        .optional {
          @apply tw-text-[10px] tw-font-normal tw-text-neutral-500;
        }
      }
    }

    // Phones stack the stepper over a full-width CTA: typed quantities make
    // long totals ("Save changes — $ 182320") the norm, and a single row would
    // clip the label. From `sm` up the row gives the button a stable width
    // (its label changes on blur-commit, right under a pending tap) and wraps
    // instead of shrinking when a long label does not fit.
    .modal-footer {
      @apply tw-flex tw-flex-shrink-0 tw-flex-col tw-items-center tw-gap-3 tw-border-t tw-border-neutral-200 tw-bg-neutral-100 tw-px-4 tw-py-3 sm:tw-flex-row sm:tw-flex-wrap sm:tw-justify-end;

      .save-button {
        @apply tw-w-full sm:tw-w-auto sm:tw-min-w-[18rem];
      }
    }
  }
</style>
